import PencomTextField from "@/components/input/PencomTextField";
import SearchableDropdown from "@/components/input/SearchableDropdown";
import { PrimaryButton } from "@/components/buttons/PrimaryButton";
import { PencomRoutes } from "@/router/PencomRoutes";
import { useNavigate } from "react-router-dom";
import { useForm } from "@tanstack/react-form";
import { PencomGenericBanner } from "@/components/banners/PencomBanner";
import { toast } from "@/utils/toast";
import {
  useLookupRC,
  useGetOrganizationTypes,
  useGetSectorCodesByOrgType,
} from "@/hooks/employer-registration/employer-registration.hook";
import { useEffect, useState } from "react";
import { EmployerRegStepIndicator } from "@/components/onboarding/EmployerRegStepIndicator";
import { useAnalytics } from "@/hooks/useAnalytics";

interface OrganizationLookupForm {
  organizationName: string;
  organizationTypeCode: string;
  sectorCode: string;
}

export const EmployerCodeRegPage = () => {
  const navigate = useNavigate();
  const lookupRCMutation = useLookupRC();
  const {
    data: organizationTypesData,
    isLoading: isLoadingOrgTypes,
  } = useGetOrganizationTypes();
  const [selectedOrgType, setSelectedOrgType] = useState<string>("");
  const { trackEvent, eventNames } = useAnalytics();

  const { data: sectorsData, isLoading: isLoadingSectors } =
    useGetSectorCodesByOrgType(selectedOrgType);

  const form = useForm({
    defaultValues: {
      organizationName: "",
      organizationTypeCode: "",
      sectorCode: "",
    } as OrganizationLookupForm,
    onSubmit: async ({ value }) => {
      lookupRCMutation.mutate(
        {
          organizationName: value.organizationName,
          organizationTypeCode: value.organizationTypeCode,
          sectorCode: value.sectorCode,
        },
        {
          onSuccess: (response) => {
            console.log("Lookup Response:", response);
            trackEvent(eventNames.onboardingStepSubmitted, {
              step: "employer_code_request",
              status: "success",
            });
            navigate(PencomRoutes.EMPLOYER_DETAILS, {
              state: {
                lookupData: response,
              },
            });
          },
          onError: (error) => {
            toast.error(error.message || "Failed to lookup. Please try again.");
            trackEvent(eventNames.onboardingStepSubmitted, {
              step: "employer_code_request",
              status: "error",
              message: error.message,
            });
          },
        },
      );
    },
  });

  useEffect(() => {
    trackEvent(eventNames.onboardingPageView, {
      step: "employer_code_request",
    });
  }, []);

  const organizationTypeOptions = (organizationTypesData ?? []).map((type) => ({
    id: type.code,
    label: type.label,
  }));

  const sectorOptions = (sectorsData?.sectors ?? []).map((sector) => ({
    id: sector.code,
    label: sector.name,
  }));

  const handleEmployerCodeNavigation = () => {
    navigate(PencomRoutes.LOGIN);
  };

  return (
    <div className="w-full flex flex-col gap-y-10 max-w-[509px]">
      <EmployerRegStepIndicator />

      <div className="flex flex-col gap-y-2 items-start">
        <h1 className="text-[40px] font-semibold font-clash-display text-medium-dark-grey tracking-[-0.6px] leading-tight">
          Employer Code Request
        </h1>
        <p className="text-medium-dark-grey text-base font-regular font-clash-display tracking-[-0.08px]">
          Enter your organisation details to begin employer code application.
        </p>
      </div>

      <div className="flex flex-col gap-y-5">
        <form.Field
          name="organizationName"
          validators={{
            onChange: ({ value }) => {
              if (!value || value.trim().length === 0) {
                return "Organisation name is required";
              }
              if (value.length < 2) {
                return "Organisation name must be at least 2 characters";
              }
              return undefined;
            },
          }}
          children={(field) => (
            <PencomTextField
              id="organization-name"
              label="Organisation Name"
              placeholder="Enter registered organisation name"
              type="text"
              bg="white"
              value={field.state.value}
              onChange={(e) => field.handleChange(e.target.value)}
              error={!!field.state.meta.errors.length}
              helperText={field.state.meta.errors.join(", ")}
            />
          )}
        />

        <form.Field
          name="organizationTypeCode"
          validators={{
            onChange: ({ value }) => {
              if (!value) {
                return "Organisation type is required";
              }
              return undefined;
            },
          }}
          children={(field) => (
            <div>
              <SearchableDropdown
                id="organization-type"
                label="Organisation Type"
                placeholder="Select your legal registration category"
                options={organizationTypeOptions}
                value={field.state.value}
                onChange={(value) => {
                  field.handleChange(value);
                  setSelectedOrgType(value);
                  form.setFieldValue("sectorCode", "");
                }}
                disabled={isLoadingOrgTypes}
                bg="white"
                helperText={field.state.meta.errors.join(", ")}
              />
            </div>
          )}
        />

        <form.Field
          name="sectorCode"
          validators={{
            onChange: ({ value }) => {
              if (!value) {
                return "Sector is required";
              }
              return undefined;
            },
          }}
          children={(field) => (
            <div>
              <SearchableDropdown
                id="sector-code"
                label="Sector"
                placeholder={
                  !selectedOrgType
                    ? "Select organisation type first"
                    : isLoadingSectors
                      ? "Loading sectors..."
                      : "Select sector"
                }
                options={sectorOptions}
                value={field.state.value}
                onChange={(value) => field.handleChange(value)}
                disabled={!selectedOrgType || isLoadingSectors}
                bg="white"
                helperText={field.state.meta.errors.join(", ")}
              />
            </div>
          )}
        />

        <PencomGenericBanner
          className="bg-white border border-stroke p-4 rounded-lg"
          children={
            <div className="flex flex-col gap-2">
              <p className="font-regular font-clash-display text-md text-medium-grey tracking-[-0.07px] leading-5">
                Select the sector that best describes your organization's
                primary business activity.
              </p>
            </div>
          }
        />

        <div className="flex flex-col gap-y-6">
          <form.Subscribe
            children={(state) => {
              const isDisabled =
                !state.values.organizationName ||
                !state.values.organizationTypeCode ||
                !state.values.sectorCode;
              return (
                <PrimaryButton
                  title={
                    lookupRCMutation.isPending
                      ? "Verifying..."
                      : "Continue to Application"
                  }
                  onClick={() => form.handleSubmit()}
                  disabled={isDisabled || form.state.isSubmitting || lookupRCMutation.isPending}
                  isLoading={lookupRCMutation.isPending}
                />
              )
            }}
          />

          <p className="font-clash-display text-medium-dark-grey text-sm font-regular text-center leading-[18px] tracking-[-0.07px]">
            <span className="text-black-dark-grey">
              Already have an Employer Code?{" "}
            </span>
            <button
              type="button"
              onClick={handleEmployerCodeNavigation}
              className="font-clash-display cursor-pointer text-primary-button underline font-semibold hover:text-light-primary-button transition-colors"
            >
              Sign in to eHub with your code
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};
